/**
 * The `Services` registry (architecture §3.7). Features register their implementation in `onBoot` by plain assignment
 * (`ctx.services.rewards = …`). Until a provider registers, each slot holds a neutral placeholder that warns once and
 * returns an empty answer (so screens of features that land early keep working); the creature animation runtime and
 * combat's auto-run have no meaningful placeholder and throw a clear error instead.
 */
import type { CardService, CombatService, CreatureService, EconomyService, ExploreService, RewardService, Services } from '../shared';

export type ServiceKey = keyof Services;
export const SERVICE_KEYS: readonly ServiceKey[] = ['rewards', 'economy', 'equipment', 'creatures', 'combat', 'cards', 'explore'];
const PROVIDER: Record<ServiceKey, string> = { rewards: 'meta', economy: 'meta', equipment: 'meta', creatures: 'creature', combat: 'combat', cards: 'cards', explore: 'explore' };

const warned = new Set<string>();
function warnOnce(key: ServiceKey, member: string): void {
  const id = `${key}.${member}`;
  if (warned.has(id)) return;
  warned.add(id);
  console.warn(`[services] ${id}: no provider registered yet (owner: ${PROVIDER[key]}); using a placeholder`);
}
const missing = (key: ServiceKey, member: string): never => {
  throw new Error(`services.${key}.${member}: not registered (the ${PROVIDER[key]} feature provides it in onBoot)`);
};

function placeholders(): Services {
  const rewards: RewardService = {
    grant: (source) => { warnOnce('rewards', `grant(${source})`); return { ledgerIds: [], lines: [] }; },
    registerSink: () => { warnOnce('rewards', 'registerSink'); },
  };
  const economy: EconomyService = {
    balance: () => { warnOnce('economy', 'balance'); return 0; },
    spend: () => { warnOnce('economy', 'spend'); return false; },
    level: () => { warnOnce('economy', 'level'); return 1; },
  };
  const creatures: CreatureService = {
    appearance:()=>null,
    createAtelierActor:async()=>missing('creatures','createAtelierActor'),
    atelierPreset:async()=>missing('creatures','atelierPreset'),
    createPreset: () => missing('creatures', 'createPreset'),
    active: () => { warnOnce('creatures', 'active'); return null; },
    get: () => { warnOnce('creatures', 'get'); return null; },
    get anim(): CreatureService['anim'] { return missing('creatures', 'anim'); },
  };
  const combat: CombatService = {
    runAuto: () => missing('combat', 'runAuto'),
    buildSpec: () => missing('combat', 'buildSpec'),
  };
  const cards: CardService = { addCards: () => { warnOnce('cards', 'addCards'); } };
  const explore: ExploreService = {
    summaryForDashboard: () => { warnOnce('explore', 'summaryForDashboard'); return { regionKey: '', stateKey: '', lastLines: [] }; },
  };
  return { rewards, economy, creatures, combat, cards, explore };
}

/** The registry: a `Services` object whose slots start as placeholders; `registered()` lists real providers. */
export function createServices(): { services: Services; registered(): ServiceKey[] } {
  const base = placeholders();
  const real = new Set<ServiceKey>();
  const store = { ...base };
  const services = {} as Services;
  for (const key of SERVICE_KEYS) {
    Object.defineProperty(services, key, {
      enumerable: true,
      get: () => store[key],
      set: (v: Services[typeof key]) => {
        if (v === null || typeof v !== 'object') throw new TypeError(`services.${key} must be an object`);
        (store as Record<ServiceKey, unknown>)[key] = v;
        real.add(key);
      },
    });
  }
  return { services, registered: () => SERVICE_KEYS.filter((k) => real.has(k)) };
}
